import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "./auth.context";
import {
    AppView,
    appViewStorageKey,
    canSwitchAppViews,
    canUseAppView,
    parseStoredAppView,
    resolveActiveAppView,
} from "../utils/appView";

interface AppViewContextI {
    activeView: AppView;
    canSwitch: boolean;
    setActiveView: (view: AppView) => void;
}

const AppViewContext = React.createContext<AppViewContextI>({
    activeView: "practitioner",
    canSwitch: false,
    setActiveView: () => undefined,
});

export const AppViewProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const organisation = user?.Organisation;
    const [savedView, setSavedView] = useState<AppView | null>(() =>
        user?.id ? parseStoredAppView(window.localStorage.getItem(appViewStorageKey(user.id))) : null,
    );

    useEffect(() => {
        if (!user?.id) {
            setSavedView(null);
            return;
        }
        setSavedView(parseStoredAppView(window.localStorage.getItem(appViewStorageKey(user.id))));
    }, [user?.id]);

    const canSwitch = canSwitchAppViews(user, organisation);
    const activeView = useMemo(
        () => resolveActiveAppView(savedView, user, organisation),
        [savedView, user, organisation],
    );

    const setActiveView = useCallback(
        (view: AppView) => {
            if (!canUseAppView(view, user, organisation)) {
                return;
            }
            if (user?.id) {
                window.localStorage.setItem(appViewStorageKey(user.id), view);
            }
            setSavedView(view);
        },
        [user, organisation],
    );

    const value = useMemo(() => ({ activeView, canSwitch, setActiveView }), [activeView, canSwitch, setActiveView]);

    return <AppViewContext.Provider value={value}>{children}</AppViewContext.Provider>;
};

export const useAppView = (): AppViewContextI => React.useContext(AppViewContext);
